import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  HeatmapChart,
  Pagination,
  Panel,
  SectionHeader,
  SegmentedControl,
  SkeletonTable,
  useClientTable,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney, formatPercent } from '@/lib/cash-flow';
import type { StatsInterval } from '@/lib/leads';
import { periodLabel } from '@/lib/report-window';
import {
  COHORT_INTERVAL_NOUN,
  COHORT_METRIC,
  COHORT_METRIC_SPEC,
  COHORT_VIEW,
  LEAD_COHORTS_QUERY_KEY,
  cohortTypicalRow,
  cohortViewLabel,
  cohortColumns,
  cohortMatrix,
  cohortRowLabels,
  cohortTableRows,
  readLeadCohorts,
  type CohortMetric,
  type CohortView,
  type CohortTableRow,
  type LeadCohorts,
} from '@/lib/lead-cohorts';

const COHORT_SORT_KEYS = ['key', 'leads', 'booked', 'bookRate', 'lostRate', 'open', 'bookedValue', 'valuePerLead', 'medianDaysToBook'] as const;
const DEFAULT_SORT: DataTableSort = { key: 'key', direction: 'desc' };
/** Past this many columns the cell labels crowd; the colors and tooltips carry it. */
const MAX_LABELED_COLUMNS = 16;
const ROW_PX = 30;
const COMPACT_ROW_PX = 16;
/** Past this many cohorts the rows go compact. */
const MANY_ROWS = 40;
const CHART_CHROME_PX = 110;
/** Cohorts change only as the five-minute lead and opportunity sync lands. */
const COHORTS_STALE_MS = 5 * 60_000;

/** The last heatmap row: the average share at each age, the number to plan with. */
const TYPICAL_ROW_LABEL = 'Typical';

const count = (value: number): string => value.toLocaleString('en-US');
const rate = (value: number | null): string => (value === null ? '–' : formatPercent(value));

function CohortTable({ cohorts }: { readonly cohorts: LeadCohorts }): React.ReactElement {
  const rows = useMemo(() => cohortTableRows(cohorts), [cohorts]);
  const table = useClientTable<CohortTableRow>({
    rows,
    searchText: (row) => periodLabel(row.key, cohorts.interval),
    sortKeys: COHORT_SORT_KEYS,
    defaultSort: DEFAULT_SORT,
    prefix: 'cohort',
  });
  return (
    <>
      <DataTable
        headers={[
          { label: 'Arrived', sortKey: 'key' },
          { label: 'Leads', sortKey: 'leads' },
          { label: 'Booked', sortKey: 'booked' },
          { label: 'Booked %', sortKey: 'bookRate' },
          { label: 'Lost %', sortKey: 'lostRate' },
          { label: 'Still open', sortKey: 'open' },
          { label: 'Booked $', sortKey: 'bookedValue' },
          { label: '$ per lead', sortKey: 'valuePerLead' },
          { label: 'Median days to book', sortKey: 'medianDaysToBook' },
        ]}
        rightAlignedHeaders={[1, 2, 3, 4, 5, 6, 7, 8]}
        sort={table.sort}
        onSort={table.onSort}
        minWidth={900}
        cards={table.pageRows.map((row) => ({
          key: row.key,
          title: periodLabel(row.key, cohorts.interval),
          subtitle: `${count(row.leads)} leads`,
          fields: [
            { label: 'Booked', value: `${count(row.booked)} (${rate(row.bookRate)})` },
            { label: 'Lost %', value: rate(row.lostRate) },
            { label: 'Booked $', value: formatMoney(row.bookedValue) },
            { label: '$ per lead', value: row.valuePerLead === null ? '–' : formatMoney(Math.round(row.valuePerLead)) },
            { label: 'Still open', value: count(row.open) },
            { label: 'Median days to book', value: row.medianDaysToBook === null ? '–' : count(Math.round(row.medianDaysToBook)) },
          ],
        }))}
      >
        {table.pageRows.map((row) => (
          <tr key={row.key} className="border-t border-portal-line align-middle">
            <td className="p-3 text-portal-small font-bold whitespace-nowrap">{periodLabel(row.key, cohorts.interval)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums">{count(row.leads)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums">{count(row.booked)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums">{rate(row.bookRate)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{rate(row.lostRate)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{count(row.open)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">{formatMoney(row.bookedValue)}</td>
            <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
              {row.valuePerLead === null ? '–' : formatMoney(Math.round(row.valuePerLead))}
            </td>
            <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">
              {row.medianDaysToBook === null ? '–' : count(Math.round(row.medianDaysToBook))}
            </td>
          </tr>
        ))}
      </DataTable>
      <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
    </>
  );
}

export interface LeadCohortsSectionProps {
  /** The page's interval: cohorts are days, weeks or months to match. */
  readonly interval: StatsInterval;
  /** The page's range start (YYYY-MM-DD): the oldest cohort holds it. */
  readonly from: string;
}

/** Rows shrink as cohorts pile up, so 36 months of weeks stays one scroll. */
function rowHeight(rows: number): number {
  return rows > MANY_ROWS ? COMPACT_ROW_PX : ROW_PX;
}

/**
 * Lead cohorts on Analytics → Leads: leads grouped by the day, week or
 * month they arrived (following the page's Range and Interval), and how far
 * each group had got by every period since (booked %, lost %, dollars booked,
 * dollars per lead). Reading down a column compares groups at the same age,
 * so a young group never looks worse just for being young. The page and the
 * API are behind view_analytics.
 */
export function LeadCohortsSection({ interval, from }: LeadCohortsSectionProps): React.ReactElement {
  const [metric, setMetric] = useState<CohortMetric>(COHORT_METRIC.bookRate);
  const [cohortView, setCohortView] = useState<CohortView>(COHORT_VIEW.eachPeriod);
  const cohorts = useQuery({
    queryKey: LEAD_COHORTS_QUERY_KEY.window(interval, from),
    queryFn: ({ signal }) => readLeadCohorts(interval, from, signal),
    staleTime: COHORTS_STALE_MS,
  });

  const spec = COHORT_METRIC_SPEC[metric];
  const data = cohorts.data;
  const view = useMemo(
    () =>
      data
        ? {
            rows: [...cohortRowLabels(data), TYPICAL_ROW_LABEL],
            columns: cohortColumns(data),
            values: [...cohortMatrix(data, metric, cohortView), cohortTypicalRow(data, metric, cohortView)],
          }
        : null,
    [data, metric, cohortView],
  );
  const totalLeads = data?.cohorts.reduce((sum, cohort) => sum + cohort.leads, 0) ?? 0;

  return (
    <div>
      <SectionHeader
        title="Lead cohorts"
        description="Leads grouped by when they arrived. Each week shows what a group booked, lost or earned in that period alone, and the Typical row is the share to plan with; Running total shows how far each group had got, to compare groups at the same age."
        aside={
          <div className="flex flex-wrap gap-2">
            <SegmentedControl<CohortView>
              label="Show"
              value={cohortView}
              onChange={setCohortView}
              options={[COHORT_VIEW.eachPeriod, COHORT_VIEW.runningTotal].map((key) => ({ key, label: cohortViewLabel(key, interval) }))}
            />
            <SegmentedControl<CohortMetric>
              label="Color by"
              value={metric}
              onChange={setMetric}
              options={(Object.keys(COHORT_METRIC_SPEC) as CohortMetric[]).map((key) => ({ key, label: COHORT_METRIC_SPEC[key].label }))}
            />
          </div>
        }
      />

      {cohorts.isError ? (
        <ErrorNotice title="Lead cohorts unavailable" message={errorMessage(cohorts.error)} />
      ) : !data || !view ? (
        <SkeletonTable rows={8} />
      ) : totalLeads === 0 ? (
        <Panel>
          <EmptyState title="No leads in this range" message="Cohorts fill in as leads arrive from the website and Salesforce." />
        </Panel>
      ) : (
        <div className="space-y-4">
          <Panel className="p-4">
            {data.capped ? (
              <p className="mb-3 text-portal-small text-portal-ink">
                Showing the newest {count(data.cohorts.length)} {COHORT_INTERVAL_NOUN[data.interval]}s. Pick Weekly or Monthly to see the whole range.
              </p>
            ) : null}
            <HeatmapChart
              title={`${spec.label}, ${cohortViewLabel(cohortView, data.interval).toLowerCase()}, by ${COHORT_INTERVAL_NOUN[data.interval]} the leads arrived and ${COHORT_INTERVAL_NOUN[data.interval]}s since`}
              rows={view.rows}
              columns={view.columns}
              values={view.values}
              unit={spec.unit}
              formatValue={spec.format}
              showValues={view.columns.length <= MAX_LABELED_COLUMNS}
              legend
              height={view.rows.length * rowHeight(view.rows.length) + CHART_CHROME_PX}
            />
            <p className="mt-2 text-portal-micro text-portal-muted">
              Each row is a group of leads (arrival date and how many). {view.columns[0]} is the {COHORT_INTERVAL_NOUN[data.interval]} they arrived; blank cells
              haven&apos;t happened yet. Typical averages the groups old enough to reach each column (rates pool their leads; dollars booked is the mean per group), so the right-hand columns rest on few groups. Booked means Contract Signed, by its signed date; dollars are the contract total. Tests and
              duplicates are left out; spam and leads nobody worked stay in, since they are part of lead quality.
            </p>
          </Panel>
          <CohortTable cohorts={data} />
        </div>
      )}
    </div>
  );
}
